// ── Firebase Configuration ────────────────────────────────────────────────────
// IMPORTANT: Add your Netlify domain to Firebase authorized domains:
// Firebase Console → Authentication → Settings → Authorized domains
// Add: your-site.netlify.app AND boisterous-marigold-5d3d07.netlify.app
// Also add localhost for local dev

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

// Only init if config is present
const hasConfig = firebaseConfig.apiKey && firebaseConfig.projectId;
const app  = hasConfig ? initializeApp(firebaseConfig) : null;
const auth = hasConfig ? getAuth(app) : null;
const db   = hasConfig ? getFirestore(app) : null;

// Set persistence to LOCAL — users stay logged in across sessions
// Firebase uses IndexedDB (like cookies but better) — persists through browser restarts
if (auth) {
  setPersistence(auth, browserLocalPersistence).catch(()=>{});
}

export { auth, db };
export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
};

// ── User helpers ──────────────────────────────────────────────────────────────

// Get or create user document in Firestore
export async function getOrCreateUser(firebaseUser) {
  if (!db) return null;
  const ref  = doc(db, 'users', firebaseUser.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      uid:              firebaseUser.uid,
      email:            firebaseUser.email,
      displayName:      firebaseUser.displayName || '',
      isPro:            false,
      stripeCustomerId: null,
      subscriptionId:   null,
      subscriptionStatus: 'inactive',
      createdAt:        serverTimestamp(),
      updatedAt:        serverTimestamp(),
    });
    return { isPro: false, subscriptionStatus: 'inactive' };
  }
  return snap.data();
}

// Check if user has active pro subscription
export async function getUserProStatus(uid) {
  if (!db || !uid) return false;
  try {
    const ref  = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return false;
    const data = snap.data();
    // Check both isPro flag AND subscriptionStatus
    // Also accept 'trialing' as active (Stripe trial period)
    const activeStatuses = ['active', 'trialing', 'paid'];
    return data.isPro === true && activeStatuses.includes(data.subscriptionStatus);
  } catch(_) {
    return false;
  }
}

// Also check by email (used when webhook fires before user is in Firestore)
export async function getUserProStatusByEmail(email) {
  if (!db || !email) return false;
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const q    = query(collection(db, 'users'), where('email', '==', email));
    const snap = await getDocs(q);
    if (snap.empty) return false;
    const data = snap.docs[0].data();
    const activeStatuses = ['active', 'trialing', 'paid'];
    return data.isPro === true && activeStatuses.includes(data.subscriptionStatus);
  } catch(_) {
    return false;
  }
}

// Mark user as pro after successful payment
export async function grantProAccess(uid, stripeData = {}) {
  if (!db || !uid) return;
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    isPro:              true,
    subscriptionStatus: 'active',
    stripeCustomerId:   stripeData.customerId   || null,
    subscriptionId:     stripeData.subscriptionId || null,
    proGrantedAt:       serverTimestamp(),
    updatedAt:          serverTimestamp(),
  });
}

// Revoke pro access (subscription cancelled/failed)
export async function revokeProAccess(uid) {
  if (!db || !uid) return;
  const ref = doc(db, 'users', uid);
  await updateDoc(ref, {
    isPro:              false,
    subscriptionStatus: 'cancelled',
    updatedAt:          serverTimestamp(),
  });
}

export const FIREBASE_ENABLED = hasConfig;
